import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const PLAN_DETAILS = {
  starter: {
    name: 'Starter',
    price: 0,
    priceLabel: 'Free',
    badge: '30-Day Free Trial',
    clientLimit: 5,
    description: 'Perfect for trainers just getting started.',
  },
  pro: {
    name: 'Pro',
    price: 49,
    priceLabel: '$49/mo',
    badge: 'Most Popular',
    clientLimit: 20,
    description: 'For growing trainers who need more clients and features.',
  },
};

const STRIPE_PUBLISHABLE_KEY = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY;
const isStripeConfigured =
  STRIPE_PUBLISHABLE_KEY && STRIPE_PUBLISHABLE_KEY !== 'your-stripe-publishable-key-here';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams?.get('plan') || 'starter';
  const priceId = searchParams?.get('priceId') || '';
  const plan = PLAN_DETAILS?.[planId] || PLAN_DETAILS?.starter;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleProceed = async () => {
    setLoading(true);
    setError(null);

    if (!isStripeConfigured) {
      // Stripe not yet configured — skip to registration for now
      setTimeout(() => {
        navigate(`/post-payment-register?plan=${planId}&session_id=demo_session`);
        setLoading(false);
      }, 600);
      return;
    }

    try {
      // When Stripe keys are added, this block will create a real Checkout Session.
      // Replace the fetch URL with your backend endpoint or Supabase Edge Function.
      //
      // Example:
      // const res = await fetch('/api/create-checkout-session', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     priceId,
      //     successUrl: `${window.location.origin}/post-payment-register?plan=${planId}&session_id={CHECKOUT_SESSION_ID}`,
      //     cancelUrl: `${window.location.origin}/pricing`,
      //     trialPeriodDays: planId === 'starter' ? 30 : undefined,
      //     allowPromotionCodes: true,
      //   }),
      // });
      // const { url } = await res.json();
      // window.location.href = url;

      // Placeholder until backend is wired:
      navigate(`/post-payment-register?plan=${planId}&session_id=demo_session`);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A0A0B' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/pricing')} className="flex items-center space-x-2 transition-colors" style={{ color: '#6B7280' }}>
            <Icon name="ArrowLeft" size={16} />
            <span className="text-sm">Back to pricing</span>
          </button>
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-7 h-7 rounded-lg" style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)' }}>
              <Icon name="Zap" size={14} color="white" />
            </div>
            <span className="font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>FormForge</span>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {/* Order Summary */}
            <div className="rounded-2xl p-6 mb-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
              <h2 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#6B7280' }}>Order Summary</h2>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-semibold" style={{ color: '#E8EAED' }}>{plan?.name} Plan</p>
                  <p className="text-sm" style={{ color: '#6B7280' }}>{plan?.description}</p>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>{plan?.priceLabel}</span>
              </div>
              {planId === 'starter' && (
                <div className="text-sm rounded-lg px-3 py-2 flex items-center space-x-2 mt-3" style={{ background: 'rgba(170,255,0,0.08)', color: '#AAFF00', border: '1px solid rgba(170,255,0,0.2)' }}>
                  <Icon name="Gift" size={14} />
                  <span>30-day free trial — no charge today</span>
                </div>
              )}
              <div className="mt-4 pt-4 flex items-center justify-between text-sm" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', color: '#6B7280' }}>
                <span>Up to {plan?.clientLimit} active clients</span>
                <span className="inline-flex items-center space-x-1 text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <Icon name="Shield" size={11} />
                  <span>Secure checkout</span>
                </span>
              </div>
            </div>

            {!isStripeConfigured && (
              <div className="rounded-xl px-4 py-3 mb-6 flex items-start space-x-3" style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.2)' }}>
                <Icon name="AlertTriangle" size={16} style={{ color: '#FF6B00' }} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#FF6B00' }}>Stripe not yet configured</p>
                  <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Add your <code className="px-1 rounded" style={{ background: 'rgba(255,255,255,0.08)' }}>VITE_STRIPE_PUBLISHABLE_KEY</code> to enable real payments.</p>
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-xl px-4 py-3 mb-6 text-sm" style={{ background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.2)', color: '#FF4444' }}>{error}</div>
            )}

            <button onClick={handleProceed} disabled={loading} className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-60 hover:opacity-90" style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)', color: '#fff', boxShadow: '0 0 20px rgba(255,107,0,0.3)' }}>
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Icon name="Lock" size={15} />
                  <span>{isStripeConfigured ? planId === 'starter' ? 'Start Free Trial' : 'Pay & Continue' : 'Continue to Registration'}</span>
                </>
              )}
            </button>

            <p className="text-center text-xs mt-4" style={{ color: '#6B7280' }}>
              By continuing you agree to our{' '}
              <span style={{ color: '#FF6B00' }} className="cursor-pointer hover:underline">Terms of Service</span>{' '}
              and{' '}
              <span style={{ color: '#00D4FF' }} className="cursor-pointer hover:underline">Privacy Policy</span>.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
